import escapeHtml from "escape-html";
import type { EChartsOption } from "echarts";
import {
  buildTooltipStyle,
  IMPRESSION_FORMATTER,
} from "@/pages/CampaignOverviewPage/charts/chartHelpers";
import {
  getChartPalette,
  type ChartPalette,
} from "@/pages/CampaignOverviewPage/charts/chartPalette";
import {
  buildPillBarOption,
  formatCompactImpressions,
} from "@/pages/CampaignOverviewPage/charts/pillBarChart";
import {
  cityLabel,
  type CityData,
  type RegionCities,
  type RegionData,
} from "@/pages/CampaignOverviewPage/utils/geoDerivation";

const GEO_TOOLTIP_CITY_LIMIT = 5;
const TOP_CITIES_LABEL_WIDTH = 130;

// Sequential single-hue scale defined by the palette's map stops (light: Purple
// 15% → Purple at 0.65 → Deep plum; dark: dim plum → Purple at 0.5 → light
// purple, so brighter still means more). The midpoint isn't always 0.5, which
// ECharts' evenly-spaced color list can't express, so the piecewise scale is
// sampled densely instead.
const GEO_SCALE_SAMPLES = 21;
const HEX_RADIX = 16;

const hexToRgb = (hex: string): number[] =>
  [1, 3, 5].map((offset) =>
    Number.parseInt(hex.slice(offset, offset + 2), HEX_RADIX)
  );

const rgbToHex = (channels: number[]): string =>
  `#${channels
    .map((channel) => Math.round(channel).toString(HEX_RADIX).padStart(2, "0"))
    .join("")}`;

export const geoScaleColor = (
  normalized: number,
  stops: ChartPalette["mapStops"]
): string => {
  const position = Math.min(1, Math.max(0, normalized));
  const upperIndex = stops.findIndex((stop) => stop.position >= position);
  const upper = stops[Math.max(upperIndex, 1)];
  const lower = stops[Math.max(upperIndex, 1) - 1];
  const span = upper.position - lower.position;
  const fraction = (position - lower.position) / span;
  const [lowerRgb, upperRgb] = [lower.color, upper.color].map(hexToRgb);
  return rgbToHex(
    lowerRgb.map(
      (channel, index) => channel + (upperRgb[index] - channel) * fraction
    )
  );
};

const sampleScaleColors = (stops: ChartPalette["mapStops"]): string[] =>
  Array.from({ length: GEO_SCALE_SAMPLES }, (_, index) =>
    geoScaleColor(index / (GEO_SCALE_SAMPLES - 1), stops)
  );

export const buildGeoMapOption = (
  regions: RegionData[],
  isDark: boolean = false,
  regionCities: RegionCities = new Map()
): EChartsOption => {
  const palette = getChartPalette(isDark);
  // State borders and the no-data fill: the card color reads as gaps between
  // states; no data sits at the low end of the scale.
  const stateStroke = palette.card;
  const noDataColor = palette.mapStops[0].color;
  return {
    geo: {
      center: [-120, 45],
      itemStyle: {
        areaColor: noDataColor,
        borderColor: stateStroke,
        borderWidth: 1,
      },
      map: "USA",
      roam: true,
      zoom: 3.0,
    },
    series: [
      {
        data: regions.map((region) => ({
          name: region.region_name,
          value: region.impressions,
        })),
        emphasis: {
          itemStyle: { areaColor: palette.mapHover },
        },
        geoIndex: 0,
        itemStyle: {
          areaColor: noDataColor,
          borderColor: stateStroke,
          borderWidth: 1,
        },
        map: "USA",
        roam: false,
        type: "map",
      },
    ],
    tooltip: {
      ...buildTooltipStyle(isDark),
      formatter: (params: unknown) => {
        const item = params as {
          name: string;
          value: number | undefined;
        };
        if (item.value === undefined || Number.isNaN(item.value)) {
          return `${escapeHtml(item.name)}: No data`;
        }

        const cities = regionCities.get(item.name) ?? [];
        const shown = cities.slice(0, GEO_TOOLTIP_CITY_LIMIT);
        const overflow = cities.length - shown.length;

        const header = `<b>${escapeHtml(item.name)}</b>: ${IMPRESSION_FORMATTER.format(item.value)} impressions`;
        if (shown.length === 0) return header;

        const rows = shown
          .map(
            (c) =>
              `${escapeHtml(c.city_name)}: ${IMPRESSION_FORMATTER.format(c.impressions)}`
          )
          .join("<br/>");
        const more = overflow > 0 ? `<br/><i>…and ${overflow} more</i>` : "";
        return `${header}<br/><br/>${rows}${more}`;
      },
      trigger: "item",
    },
    // Colors regions only — the key is rendered in the DOM next to the map
    // (150 × 8 "Fewer → More impressions" bar), so the built-in legend is hidden.
    visualMap: {
      inRange: { color: sampleScaleColors(palette.mapStops) },
      max: Math.max(...regions.map((r) => r.impressions), 1),
      min: 0,
      show: false,
    },
  };
};

export const buildTopCitiesBarOption = (
  cities: CityData[],
  isDark: boolean
): EChartsOption =>
  buildPillBarOption({
    formatValue: formatCompactImpressions,
    isDark,
    labelWidth: TOP_CITIES_LABEL_WIDTH,
    rows: cities.map((city) => ({
      label: cityLabel(city),
      value: city.impressions,
    })),
  });
